import {
  Check,
  Ellipsis,
  ImageUp,
  Info,
  KeyRound,
  Link2Off,
  Loader2,
  NotebookPen,
  Pencil,
  Pin,
  PinOff,
  ScanText,
  Star,
  Trash2,
} from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import ReactPlayer from 'react-player'
import { toast } from 'sonner'

import {
  getProductById,
  requestProductNotebooklm,
  requestProductOcr,
  unlinkProductNotebooklm,
  updateEpisodeProgress,
  uploadProductThumbnail,
} from '@/api/api'
import { EpisodeList, Tag } from '@/api/type'
import { AuthImage } from '@/components/ui/auth-image'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { useSignedUrl } from '@/hooks/useSignedUrl'
import { useSubmitLock } from '@/hooks/useSubmitLock'
import { parseUtcDate } from '@/lib/date-utils'
import { formatBytes } from '@/lib/utils'
import { useAppStore } from '@/store/useAppStore'
import { useNotebooklmStore } from '@/store/useNotebooklmStore'
import { useOcrJobStore } from '@/store/useOcrJobStore'
import { useShortcutStore } from '@/store/useShortcutStore'
import { HIDDEN_MIME_TYPES } from '@/types/ebook-conversion'

import { GrantAccessDialog } from './grant-access-dialog'
import { InfoContainer } from './info-container'
import { ProductDownloadBadge } from './product-download-badge'
import { ProductTitleEditDialog } from './product-title-edit-dialog'

// NotebookLM がアップロードを受け付ける MIME（音声・動画は接頭辞で判定）
const NOTEBOOKLM_MIME_TYPES = new Set([
  'application/pdf',
  'application/epub+zip',
  'text/csv',
  'text/markdown',
  'text/plain',
])
const NOTEBOOKLM_MIME_PREFIXES = ['audio/', 'video/']
// 曲単位で大量にあり、ノートブックの用途に合わないドメイン
const NOTEBOOKLM_EXCLUDED_DOMAINS = new Set(['apple_music'])

const isNotebooklmUploadable = (mimeType: string | null | undefined): boolean => {
  if (!mimeType) return false
  if (NOTEBOOKLM_MIME_TYPES.has(mimeType)) return true
  return NOTEBOOKLM_MIME_PREFIXES.some((prefix) => mimeType.startsWith(prefix))
}

// 1週間以内にアップロードされたかどうかを判定
const isWithinOneWeek = (dateString: string | null | undefined): boolean => {
  if (!dateString) return false
  const uploadTimestamp = parseUtcDate(dateString).getTime()
  if (Number.isNaN(uploadTimestamp)) return false
  const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000
  return uploadTimestamp >= oneWeekAgo
}

type ComicItemProps = {
  target: {
    domain_product_id: string
    product_id: number
    title: string
    thumbnail?: string | null
    /** この作品に含まれるお気に入りエピソードの件数 */
    favorite_episode_count?: number
    /** 先頭エピソードの ID */
    first_episode_id?: number | null
    episodes?: EpisodeList[]
    progress?: number
    is_completed?: boolean
    description?: string | null
    tags?: Tag[]
  }
  domain: string
  index: number
  isVideoDomain: boolean
  onItemClick: (productId: number) => void
  /** 単話作品の先頭エピソードをお気に入りトグルする */
  onFavoriteToggle?: (episodeId: number, currentFavorite: boolean) => void
  isDeleteMode?: boolean
  isSelectedForDeletion?: boolean
  onToggleDeleteSelection?: (productId: number) => void
  onDeleteProduct?: (productId: number, title: string) => void
  /** タイトル変更を一覧側に反映する */
  onTitleUpdated?: (productId: number, title: string) => void
  aspectRatio?: 'landscape' | 'portrait'
  /** trueの場合、サムネイル画像を非表示にしてプレースホルダーを表示（メモリ節約） */
  hideImage?: boolean
}

export const ComicItem = ({
  target,
  domain,
  index,
  isVideoDomain,
  onItemClick,
  onFavoriteToggle,
  isDeleteMode,
  isSelectedForDeletion,
  onToggleDeleteSelection,
  onDeleteProduct,
  onTitleUpdated,
  aspectRatio = 'portrait',
  hideImage = false,
}: ComicItemProps) => {
  // 動画のベースURL（旧構造を維持 - 外部ファイルアクセス用）
  const baseUrl = `/products/${domain}/${target.domain_product_id}/${target.domain_product_id}`
  const videoPath = isVideoDomain ? `${baseUrl}/${target.domain_product_id}.mp4` : null
  const { url: signedVideoUrl } = useSignedUrl(videoPath)

  // 動画再生状態（lightモードから切り替わったかどうか）
  const [isVideoActive, setIsVideoActive] = useState(false)

  // Info BottomSheetの開閉状態
  const [isInfoOpen, setIsInfoOpen] = useState(false)
  // タグ編集での更新を反映するためのローカルタグ状態
  const [tags, setTags] = useState<Tag[]>(target.tags ?? [])

  // サムネイル変更（管理者のみ）
  const hasFullAccess = useAppStore((s) => s.hasFullAccess)
  const fileInputRef = useRef<HTMLInputElement>(null)
  // サムネイル差し替え後に AuthImage を再マウントさせるためのバージョン
  const [thumbnailVersion, setThumbnailVersion] = useState(0)
  // アクセス権限付与ダイアログの開閉状態
  const [grantDialogOpen, setGrantDialogOpen] = useState(false)
  // タイトル変更ダイアログの開閉状態
  const [titleDialogOpen, setTitleDialogOpen] = useState(false)
  const { isSubmitting: isOcrSubmitting, run: runOcrRequest } = useSubmitLock()
  // ワーカーから SSE で届く進捗
  const ocrJob = useOcrJobStore((s) => s.jobs.get(target.product_id))

  // NotebookLM 連携（管理者のみ）
  const { isSubmitting: isNotebooklmSubmitting, run: runNotebooklmRequest } = useSubmitLock()
  const notebooklmJob = useNotebooklmStore((s) => s.jobs.get(target.product_id))
  const notebooklmUrl = useNotebooklmStore((s) => s.links.get(target.product_id))
  const ensureNotebooklmLinks = useNotebooklmStore((s) => s.ensureLinksLoaded)
  const ensureNotebooklmJobs = useNotebooklmStore((s) => s.ensureActiveJobsLoaded)
  const removeNotebooklmLink = useNotebooklmStore((s) => s.removeLink)

  useEffect(() => {
    if (!hasFullAccess) return
    void ensureNotebooklmLinks()
    // リロード直後は SSE が来ないため、実行中ジョブを取り直して取り込み中を出せるようにする
    void ensureNotebooklmJobs()
  }, [hasFullAccess, ensureNotebooklmLinks, ensureNotebooklmJobs])

  // 動画進捗保存用
  const durationRef = useRef<number>(0)
  const lastSavedProgressRef = useRef<number>(0)

  // 最初のエピソードのIDを取得（動画の進捗保存用）
  const firstEpisodeId = target.episodes?.[0]?.episode_id

  // 進捗を保存する関数
  const saveProgress = useCallback(
    async (progressSeconds: number, totalSeconds: number) => {
      if (!firstEpisodeId || totalSeconds <= 0) return
      try {
        const isCompleted = progressSeconds >= totalSeconds - 1
        await updateEpisodeProgress(firstEpisodeId, {
          progress: Math.floor(progressSeconds),
          is_completed: isCompleted,
        })
        lastSavedProgressRef.current = progressSeconds
      } catch (error) {
        console.error('Failed to save video progress:', error)
      }
    },
    [firstEpisodeId],
  )

  // 動画の進捗ハンドラー（5秒ごとに保存）
  const handleVideoProgress = useCallback(
    (event: React.SyntheticEvent<HTMLVideoElement>) => {
      if (durationRef.current <= 0) return
      const playedSeconds = event.currentTarget.currentTime

      // 5秒以上進んだら即座に保存
      if (Math.abs(playedSeconds - lastSavedProgressRef.current) >= 5) {
        saveProgress(playedSeconds, durationRef.current)
      }
    },
    [saveProgress],
  )

  const handleVideoDuration = useCallback((event: React.SyntheticEvent<HTMLVideoElement>) => {
    durationRef.current = event.currentTarget.duration
  }, [])

  const handleVideoEnded = useCallback(() => {
    // 動画終了時は100%として保存
    if (durationRef.current > 0 && firstEpisodeId) {
      saveProgress(durationRef.current, durationRef.current)
    }
  }, [firstEpisodeId, saveProgress])

  const handleClick = () => {
    if (isDeleteMode) {
      onToggleDeleteSelection?.(target.product_id)
      return
    }
    if (!isVideoDomain) {
      onItemClick(target.product_id)
    }
  }

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    const episodeId = target.first_episode_id ?? firstEpisodeId
    if (!episodeId) return
    onFavoriteToggle?.(episodeId, favoriteCount > 0)
  }

  const isProductShortcut = useShortcutStore((s) => s.shortcutProductIds.includes(target.product_id))
  const { addProductShortcut, removeProductShortcut } = useShortcutStore()

  const handleShortcutClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isProductShortcut) {
      removeProductShortcut(target.product_id)
    } else {
      addProductShortcut(target.product_id)
    }
  }

  // Infoを開いた時にプロダクト詳細APIを呼んでタグを取得（一覧APIではタグが含まれないため）
  useEffect(() => {
    if (!isInfoOpen) return
    let cancelled = false
    getProductById(target.product_id)
      .then((product) => {
        if (cancelled) return
        setTags(product.tags ?? [])
      })
      .catch((error) => {
        console.error('Failed to fetch product detail for info:', error)
      })
    return () => {
      cancelled = true
    }
  }, [isInfoOpen, target.product_id])

  const handleInfoClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsInfoOpen(true)
  }

  const handleChangeThumbnailClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    fileInputRef.current?.click()
  }

  const handleThumbnailFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (!file.type.startsWith('image/')) {
      toast.error('画像ファイルを選択してください')
      return
    }
    try {
      await uploadProductThumbnail(target.product_id, file)
      setThumbnailVersion((v) => v + 1)
      toast.success('サムネイルを変更しました')
    } catch (error) {
      console.error('Failed to update thumbnail:', error)
      toast.error('サムネイルの変更に失敗しました')
    }
  }

  const visibleEpisodes = target.episodes?.filter((ep) => !ep.mime_type || !HIDDEN_MIME_TYPES.has(ep.mime_type)) ?? []
  const totalEpisodes = visibleEpisodes.length
  const isSingleEpisode = totalEpisodes === 1
  const isAudiobook = domain === 'audiobook'
  const favoriteCount = target.favorite_episode_count ?? 0
  // 単話作品のみカードから直接お気に入りを切り替えられる（複数話はエピソード一覧から操作する）
  const canToggleFavorite = isSingleEpisode && (target.first_episode_id ?? firstEpisodeId) != null

  // 複数フォーマット対応: 各フォーマットごとの進捗を計算し、最大のものを表示
  const { progressPercent, isRead } = (() => {
    const episodes = target.episodes ?? []
    if (episodes.length === 0) return { progressPercent: 0, isRead: false }

    // フォーマット（mime_type）ごとにエピソードをグループ化
    const formatGroups = episodes.reduce(
      (acc, ep) => {
        const mimeType = ep.mime_type ?? 'unknown'
        if (!acc[mimeType]) acc[mimeType] = []
        acc[mimeType].push(ep)
        return acc
      },
      {} as Record<string, typeof episodes>,
    )

    const formatKeys = Object.keys(formatGroups)
    const hasMultipleFormats = formatKeys.length > 1

    if (hasMultipleFormats) {
      // 複数フォーマットの場合: 各フォーマットの進捗率を計算し、最大のものを使用
      let maxProgressPercent = 0
      let maxIsRead = false

      for (const format of formatKeys) {
        const formatEpisodes = formatGroups[format]
        const totalPages = formatEpisodes.reduce((sum, ep) => sum + (ep.num_pages ?? 0), 0)
        const progressPages = formatEpisodes.reduce((sum, ep) => sum + (ep.progress ?? 0), 0)
        const percent = totalPages > 0 ? Math.round((progressPages / totalPages) * 100) : 0
        const read = totalPages > 0 && progressPages >= totalPages

        if (percent > maxProgressPercent) {
          maxProgressPercent = percent
          maxIsRead = read
        }
      }

      return { progressPercent: maxProgressPercent, isRead: maxIsRead }
    } else {
      // 単一フォーマットの場合: 全エピソードの合計で計算
      const totalPages = episodes.reduce((sum, ep) => sum + (ep.num_pages ?? 0), 0)
      const progressPages = episodes.reduce((sum, ep) => sum + (ep.progress ?? 0), 0)
      const percent = totalPages > 0 ? Math.round((progressPages / totalPages) * 100) : 0
      const read = totalPages > 0 && progressPages >= totalPages

      return { progressPercent: percent, isRead: read }
    }
  })()

  // エピソードの中に1つでもNEWがあればProduct全体をNEWとする
  const hasNewEpisode = target.episodes?.some((ep) => isWithinOneWeek(ep.upload_date)) ?? false

  // PDF を含む作品だけ再OCR できる
  const hasPdfEpisode = target.episodes?.some((ep) => ep.mime_type === 'application/pdf') ?? false
  const isOcrRunning = ocrJob != null && (ocrJob.status === 'pending' || ocrJob.status === 'running')

  const handleRequestOcr = () =>
    void runOcrRequest(async () => {
      try {
        await requestProductOcr(target.product_id)
        toast.success('再OCR をキューに登録しました')
      } catch (error) {
        console.error('Failed to request OCR:', error)
        toast.error('再OCR の登録に失敗しました')
      }
    })

  // NotebookLM に取り込めるファイルを含む作品だけ導線を出す
  const canUseNotebooklm =
    !NOTEBOOKLM_EXCLUDED_DOMAINS.has(domain) &&
    (target.episodes?.some((ep) => isNotebooklmUploadable(ep.mime_type)) ?? false)
  const isNotebooklmRunning =
    notebooklmJob != null && (notebooklmJob.status === 'pending' || notebooklmJob.status === 'running')

  const handleRequestNotebooklm = () =>
    void runNotebooklmRequest(async () => {
      try {
        await requestProductNotebooklm(target.product_id)
        toast.success('NotebookLM への取り込みをキューに登録しました')
      } catch (error) {
        console.error('Failed to request NotebookLM ingest:', error)
        toast.error('NotebookLM の取り込み登録に失敗しました')
      }
    })

  const handleOpenNotebooklm = () => {
    if (!notebooklmUrl) return
    window.open(notebooklmUrl, '_blank', 'noopener,noreferrer')
  }

  const handleUnlinkNotebooklm = () =>
    void runNotebooklmRequest(async () => {
      try {
        await unlinkProductNotebooklm(target.product_id)
        removeNotebooklmLink(target.product_id)
        toast.success('NotebookLM の紐づけを解除しました')
      } catch (error) {
        console.error('Failed to unlink NotebookLM notebook:', error)
        toast.error('紐づけの解除に失敗しました')
      }
    })

  return (
    <div
      onClick={handleClick}
      className={`relative group overflow-hidden bg-gray-200 dark:bg-gray-800 active:opacity-80 transition-opacity border-[0.5px] border-black/5 dark:border-white/5 ${aspectRatio === 'portrait' ? 'aspect-2/3' : 'aspect-video'}`}
    >
      {hideImage ? (
        <div className="w-full h-full bg-muted" />
      ) : isVideoDomain && signedVideoUrl ? (
        <ReactPlayer
          playsInline
          src={signedVideoUrl}
          controls
          width={'100%'}
          height={'100%'}
          light={
            <AuthImage
              src={`/api/p/${target.product_id}/thumbnail`}
              alt="Thumbnail"
              className="w-full h-full object-cover"
            />
          }
          onClickPreview={() => setIsVideoActive(true)}
          onTimeUpdate={handleVideoProgress}
          onDurationChange={handleVideoDuration}
          onEnded={handleVideoEnded}
        />
      ) : !isVideoDomain ? (
        <AuthImage
          key={`image-${target.product_id}-${thumbnailVersion}`}
          src={target.thumbnail || `/api/p/${target.product_id}/thumbnail`}
          alt={`Image ${index + 1}`}
          className="w-full h-full object-contain"
          lazy={false}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-muted animate-pulse">
          <span className="text-muted-foreground text-xs">読み込み中...</span>
        </div>
      )}

      {/* メニューボタン（お気に入り＋ショートカット） */}
      {/* modal にするとメニューから開いたダイアログと body の pointer-events 制御が競合して画面が操作不能になる */}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <button
            onClick={(e) => e.stopPropagation()}
            className="absolute top-1 right-0 p-0! transition-transform active:scale-90 z-20"
          >
            <div className="p-1 rounded-full shadow-md bg-black/60">
              <Ellipsis size={14} className="text-white/90" />
            </div>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          {canToggleFavorite && (
            <DropdownMenuItem onClick={handleFavoriteClick}>
              <Star size={14} fill={favoriteCount > 0 ? 'currentColor' : 'none'} />
              {favoriteCount > 0 ? 'お気に入り解除' : 'お気に入りに追加'}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={handleShortcutClick}>
            {isProductShortcut ? <PinOff size={14} /> : <Pin size={14} />}
            {isProductShortcut ? 'ショートカット解除' : 'ショートカットに追加'}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleInfoClick}>
            <Info size={14} />
            情報を見る
          </DropdownMenuItem>
          {hasFullAccess && (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation()
                setTitleDialogOpen(true)
              }}
            >
              <Pencil size={14} />
              タイトルを変更
            </DropdownMenuItem>
          )}
          {hasFullAccess && (
            <DropdownMenuItem onClick={handleChangeThumbnailClick}>
              <ImageUp size={14} />
              サムネイルを変更
            </DropdownMenuItem>
          )}
          {hasFullAccess && (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation()
                setGrantDialogOpen(true)
              }}
            >
              <KeyRound size={14} />
              アクセス権を追加
            </DropdownMenuItem>
          )}
          {hasFullAccess && hasPdfEpisode && (
            <DropdownMenuItem
              disabled={isOcrRunning || isOcrSubmitting}
              onClick={(e) => {
                e.stopPropagation()
                handleRequestOcr()
              }}
            >
              <ScanText size={14} />
              {isOcrRunning ? '再OCR 実行中' : '再OCR (Yomitoku)'}
            </DropdownMenuItem>
          )}
          {hasFullAccess && notebooklmJob && isNotebooklmRunning && (
            <DropdownMenuItem disabled>
              <Loader2 size={14} className="animate-spin" />
              {notebooklmJob.totalSources > 0
                ? `NotebookLM 取り込み中 (${notebooklmJob.processedSources}/${notebooklmJob.totalSources})`
                : 'NotebookLM 取り込み中'}
            </DropdownMenuItem>
          )}
          {hasFullAccess && canUseNotebooklm && !notebooklmUrl && !isNotebooklmRunning && (
            <DropdownMenuItem
              disabled={isNotebooklmSubmitting}
              onClick={(e) => {
                e.stopPropagation()
                handleRequestNotebooklm()
              }}
            >
              <NotebookPen size={14} />
              NotebookLM に取り込む
            </DropdownMenuItem>
          )}
          {hasFullAccess && notebooklmUrl && (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation()
                handleOpenNotebooklm()
              }}
            >
              <NotebookPen size={14} />
              NotebookLM で開く
            </DropdownMenuItem>
          )}
          {hasFullAccess && notebooklmUrl && !isNotebooklmRunning && (
            <DropdownMenuItem
              disabled={isNotebooklmSubmitting}
              onClick={(e) => {
                e.stopPropagation()
                handleUnlinkNotebooklm()
              }}
            >
              <Link2Off size={14} />
              NotebookLM の紐づけを解除
            </DropdownMenuItem>
          )}
          {hasFullAccess && onDeleteProduct && (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation()
                onDeleteProduct(target.product_id, target.title)
              }}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 size={14} />
              このプロダクトを削除
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* サムネイル変更用の隠しファイル入力（管理者のみ） */}
      {hasFullAccess && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onClick={(e) => e.stopPropagation()}
          onChange={handleThumbnailFileChange}
        />
      )}

      {/* NEWバッジ / 完結バッジ / お気に入りバッジ / ダウンロードバッジ - 動画再生中は非表示 */}
      {!isVideoActive && (
        <div className="absolute top-1 left-1 z-10 flex flex-col items-start gap-0.5">
          {hasNewEpisode && (
            <span className="bg-zinc-700/80 text-emerald-400 text-[9px] font-medium tracking-wide px-1.5 py-0.5 rounded border border-emerald-500/30">
              NEW
            </span>
          )}
          {target.is_completed && (
            <span className="bg-zinc-700/80 text-amber-400 text-[9px] font-medium tracking-wide px-1.5 py-0.5 rounded border border-amber-500/30">
              完結
            </span>
          )}
          {favoriteCount > 0 && (
            <span className="inline-flex items-center gap-0.5 bg-zinc-700/80 text-amber-400 text-[9px] font-medium tracking-wide px-1.5 py-0.5 rounded border border-amber-500/30">
              <Star size={9} fill="currentColor" />
              {favoriteCount}
            </span>
          )}
          {isSingleEpisode && !isAudiobook && target.episodes && target.episodes.length > 0 && (
            <ProductDownloadBadge
              episodes={target.episodes}
              productId={target.product_id}
              domain={domain}
              className="static"
            />
          )}
        </div>
      )}

      {/* タイトルオーバーレイ - 動画再生中は非表示 */}
      {!isVideoActive && (
        <div className="absolute inset-x-0 bottom-0 pt-8 pb-2 px-1.5 bg-linear-to-t from-black/90 via-black/40 to-transparent pointer-events-none">
          <div className="flex flex-col gap-1">
            <span className="inline-flex items-center w-fit px-1.5 py-0.5 rounded-sm bg-white/20 text-[8px] font-bold text-white uppercase tracking-tight border border-white/30 shadow-sm">
              {isAudiobook ? (
                <span className="text-[10px] font-normal normal-case">
                  {formatBytes(visibleEpisodes.reduce((sum, ep) => sum + (ep.file_size ?? 0), 0))}
                </span>
              ) : (
                <>
                  {isSingleEpisode ? 'Single' : `${totalEpisodes} eps`}
                  {isSingleEpisode && target.episodes?.[0]?.file_size != null && (
                    <span className="ml-1 text-[10px] font-normal normal-case">
                      {formatBytes(target.episodes[0].file_size)}
                    </span>
                  )}
                </>
              )}
            </span>
            <h3 className="text-[10px] font-bold text-white line-clamp-2 leading-[1.1] drop-shadow-[0_1px_1px_rgba(0,0,0,1)]">
              {target.title}
            </h3>
          </div>
        </div>
      )}

      {/* オーディオブック用オフラインダウンロードバッジ */}
      {!isVideoActive && domain === 'audiobook' && target.episodes && target.episodes.length > 0 && (
        <ProductDownloadBadge episodes={target.episodes} productId={target.product_id} domain={domain} />
      )}

      {/* 進捗バー（最下部） - 動画再生中は非表示 */}
      {!isVideoActive && progressPercent > 0 && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/20">
          <div
            className={`h-full transition-all duration-700 ${isRead ? 'bg-green-500' : 'bg-blue-500'}`}
            style={{ width: `${Math.min(100, progressPercent)}%` }}
          />
        </div>
      )}

      {/* 再OCR の進捗（実行中のみ表示） */}
      {ocrJob && isOcrRunning && (
        <div className="absolute inset-x-0 bottom-0 z-30 bg-black/75 px-1.5 py-1">
          <div className="flex items-center justify-between text-[9px] font-medium text-white">
            <span>OCR {ocrJob.progress}%</span>
            {ocrJob.totalPages > 0 && (
              <span>
                {ocrJob.processedPages}/{ocrJob.totalPages}p
              </span>
            )}
          </div>
          <div className="mt-0.5 h-0.5 bg-white/20">
            <div className="h-full bg-emerald-400 transition-all" style={{ width: `${ocrJob.progress}%` }} />
          </div>
        </div>
      )}

      {/* NotebookLM 取り込みの進捗（実行中のみ表示） */}
      {notebooklmJob && isNotebooklmRunning && !isOcrRunning && (
        <div className="absolute inset-x-0 bottom-0 z-30 bg-black/75 px-1.5 py-1">
          <div className="flex items-center justify-between text-[9px] font-medium text-white">
            <span>NotebookLM {notebooklmJob.progress}%</span>
            {notebooklmJob.totalSources > 0 && (
              <span>
                {notebooklmJob.processedSources}/{notebooklmJob.totalSources}
              </span>
            )}
          </div>
          <div className="mt-0.5 h-0.5 bg-white/20">
            <div className="h-full bg-sky-400 transition-all" style={{ width: `${notebooklmJob.progress}%` }} />
          </div>
        </div>
      )}

      {/* 削除モード時のオーバーレイ */}
      {isDeleteMode && (
        <div className="absolute inset-0 bg-black/30 flex items-center justify-center z-30">
          <div
            className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-colors ${
              isSelectedForDeletion ? 'bg-red-500 border-red-500' : 'bg-white/20 border-white/60'
            }`}
          >
            {isSelectedForDeletion && <Check size={18} className="text-white" />}
          </div>
        </div>
      )}

      {/* アクセス権限付与ダイアログ（管理者のみ表示／開閉時のみマウント） */}
      {hasFullAccess && grantDialogOpen && (
        <span onClick={(e) => e.stopPropagation()}>
          <GrantAccessDialog
            open={grantDialogOpen}
            onOpenChange={setGrantDialogOpen}
            mode="product"
            targetIds={[target.product_id]}
            targetLabel={target.title}
          />
        </span>
      )}

      {/* タイトル変更ダイアログ（管理者のみ表示／開閉時のみマウント） */}
      {hasFullAccess && titleDialogOpen && (
        <span onClick={(e) => e.stopPropagation()}>
          <ProductTitleEditDialog
            open={titleDialogOpen}
            onOpenChange={setTitleDialogOpen}
            productId={target.product_id}
            currentTitle={target.title}
            onUpdated={(title) => onTitleUpdated?.(target.product_id, title)}
          />
        </span>
      )}

      {/* 情報表示用 BottomSheet（クリック伝播を止めてカードのクリックを発火させない） */}
      <span onClick={(e) => e.stopPropagation()}>
        <InfoContainer
          isOpen={isInfoOpen}
          onClose={() => setIsInfoOpen(false)}
          title={target.title}
          description={target.description ?? ''}
          tags={tags}
          numPages={target.episodes?.[0]?.num_pages ?? 0}
          mimeType={target.episodes?.[0]?.mime_type}
          fileSize={target.episodes?.[0]?.file_size}
          uploadDate={target.episodes?.[0]?.upload_date ?? ''}
          domain={domain}
          productId={target.product_id}
          episodeId={target.episodes?.[0]?.episode_id}
          onTagsUpdated={setTags}
        />
      </span>
    </div>
  )
}
